/** @jsxImportSource @emotion/react */
import { StatisticsDateRangePicker } from '../../components/StatisticsDateRangePicker';
import { useStatisticsDateRange } from '../../hooks/useStatisticsDateRange';
import { css } from '@emotion/react';
import { useState, useEffect, useCallback, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import { Select, Card, message, Spin, Button, Typography, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { AccessSessionTable } from '../components/AccessSessionTable';
import {
  getAccessFrequencyChartData, getAccessFrequencyListData, exportAccessFrequencyExcel,
  AccessListResponse, AccessExportMeta, AccessExportFilterItem,
} from '../../services/statistics.service';
import { FEATURE_OPTIONS, FREQUENCY_RANGE_OPTIONS } from './filterOptions';
import { buildAccessFrequencyChartOption, PRIMARY_BAR_COLOR } from '../components/accessChartOptions';

const { Title } = Typography;

const VIEW_BY_OPTIONS = [
  { value: 'frequency_system', label: 'Tần suất truy cập hệ thống của người dùng' },
  { value: 'frequency_feature', label: 'Tần suất truy cập lại hệ thống theo chức năng (truy cập từ 2 lần trở lên)' },
  { value: 'frequency_return_users', label: 'Số người dùng truy cập lại hệ thống (truy cập từ 2 lần trở lên)' },
];

export interface StatisticsAccessFrequencyPageHandle {
  handleExport: () => Promise<void>;
}

interface Props {
  period?: string;
  dateRange?: [string | null, string | null];
  subTabs?: React.ReactNode;
  onExportAvailabilityChange?: (ready: boolean) => void;
}

export const StatisticsAccessFrequencyPage = forwardRef<StatisticsAccessFrequencyPageHandle, Props>(
  ({ period: initialPeriod = 'all', dateRange: initialDateRange = [null, null], subTabs, onExportAvailabilityChange }, ref) => {
    const [viewBy, setViewBy] = useState('frequency_system');
    const { period, dateRange, changePeriod: handlePeriodChange,
      applyDateRange } = useStatisticsDateRange(initialPeriod, initialDateRange);
    const chartVersion = useRef(0);
    const listVersion = useRef(0);
    const [chartData, setChartData] = useState<any>(null);
    const [listData, setListData] = useState<AccessListResponse | undefined>();
    const [chartLoading, setChartLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [chartFailed, setChartFailed] = useState(false);
    const [listFailed, setListFailed] = useState(false);
    const [, setExportLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const chartRef = useRef<any>(null);

    const [filterFeatures, setFilterFeatures] = useState<string[]>([]);
    const [filterFreqRanges, setFilterFreqRanges] = useState<string[]>([]);

    const fetchChart = useCallback(async () => {
      const version = ++chartVersion.current;
      setChartFailed(false);
      setChartLoading(true);
      try {
        const data = await getAccessFrequencyChartData({
          period: period !== 'custom' ? period : undefined,
          from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
          to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
          view_by: viewBy,
          features: filterFeatures,
          frequency_ranges: filterFreqRanges,
        });
        if (version !== chartVersion.current) return;
        setChartData(data);
      } catch {
        if (version !== chartVersion.current) return;
        setChartFailed(true);
        message.error('Không thể tải biểu đồ');
      }
      finally {
        if (version === chartVersion.current) setChartLoading(false);
      }
    }, [viewBy, period, dateRange, filterFeatures, filterFreqRanges]);

    const fetchList = useCallback(async () => {
      const version = ++listVersion.current;
      setListFailed(false);
      setListLoading(true);
      try {
        const res = await getAccessFrequencyListData({
          period: period !== 'custom' ? period : undefined,
          from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
          to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
          view_by: viewBy,
          features: filterFeatures,
          frequency_ranges: filterFreqRanges,
          page,
          limit: pageSize,
        });
        if (version !== listVersion.current) return;
        setListData(res);
      } catch {
        if (version !== listVersion.current) return;
        setListFailed(true);
        message.error('Không thể tải danh sách');
      }
      finally {
        if (version === listVersion.current) setListLoading(false);
      }
    }, [viewBy, period, dateRange, filterFeatures, filterFreqRanges, page, pageSize]);

    useEffect(() => {
      onExportAvailabilityChange?.(!chartLoading && !listLoading && !chartFailed && !listFailed);
    }, [chartLoading, listLoading, chartFailed, listFailed, onExportAvailabilityChange]);

    useEffect(() => { setPage(1); }, [viewBy, period, dateRange, filterFeatures, filterFreqRanges]);
    useEffect(() => { fetchChart(); return () => { chartVersion.current += 1; }; }, [fetchChart]);
    useEffect(() => { fetchList(); return () => { listVersion.current += 1; }; }, [fetchList]);

    const chartTitle = VIEW_BY_OPTIONS.find(o => o.value === viewBy)?.label || 'Tần suất truy cập hệ thống';

    const handleExport = async () => {
      if (chartLoading || listLoading || chartFailed || listFailed) return;
      setExportLoading(true);
      try {
        let chartImg: string | undefined;
        if (chartRef.current) {
          try {
            chartImg = chartRef.current.getEchartsInstance().getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
          } catch {
            // ignore
          }
        }

        const viewByLabel = VIEW_BY_OPTIONS.find(o => o.value === viewBy)?.label || '';

        const filters: AccessExportFilterItem[] = [];
        if (viewBy === 'frequency_system' || viewBy === 'frequency_feature') {
          filters.push({
            label: 'Chức năng hệ thống:',
            value: filterFeatures.length
              ? filterFeatures.map(f => FEATURE_OPTIONS.find(o => o.value === f)?.label || f).join(', ')
              : '-',
          });
        }
        if (viewBy === 'frequency_feature') {
          filters.push({
            label: 'Tần suất truy cập lại:',
            value: filterFreqRanges.length
              ? filterFreqRanges.map(r => FREQUENCY_RANGE_OPTIONS.find(o => o.value === r)?.label || r).join(', ')
              : '-',
          });
        }

        let timeRangeLabel = 'Tất cả';
        if (period === 'today') {
          timeRangeLabel = `${dayjs().format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'this_month') {
          timeRangeLabel = `${dayjs().startOf('month').format('DD/MM/YYYY')} - ${dayjs().endOf('month').format('DD/MM/YYYY')}`;
        } else if (period === 'this_year') {
          timeRangeLabel = `${dayjs().startOf('year').format('DD/MM/YYYY')} - ${dayjs().endOf('year').format('DD/MM/YYYY')}`;
        } else if (period === 'custom' && (dateRange[0] || dateRange[1])) {
          timeRangeLabel = `${dateRange[0] ? dayjs(dateRange[0]).format('DD/MM/YYYY') : '...'} - ${dateRange[1] ? dayjs(dateRange[1]).format('DD/MM/YYYY') : '...'}`;
        }

        const meta: AccessExportMeta = {
          tab_title: 'Lượt truy cập',
          view_by_label: viewByLabel,
          chart_title: chartTitle,
          filters,
          time_range_label: timeRangeLabel,
        };

        const blob = await exportAccessFrequencyExcel({
          period: period !== 'custom' ? period : undefined,
          from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
          to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
          view_by: viewBy,
          features: filterFeatures,
          frequency_ranges: filterFreqRanges,
          page: 1,
          limit: 999999,
        }, chartImg, meta);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `thong_ke_tan_suat_${Date.now()}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch { message.error('Xuất Excel thất bại'); }
      finally { setExportLoading(false); }
    };

    useImperativeHandle(ref, () => ({
      handleExport,
    }));

    const chartOption = useMemo(() => {
      return buildAccessFrequencyChartOption(chartData);
    }, [chartData]);

    return (
      <div>
        <Card css={mainCardStyle}>
          {/* Sub-tabs (Tổng quan / Người dùng / Tần suất / Thời gian sử dụng) */}
          {subTabs}

          {/* 1. "Xem theo ▾" Select */}
          <div css={viewByRowStyle}>
            <Select
              value={viewBy}
              onChange={(val) => {
                setViewBy(val);
                handlePeriodChange('all');
                setFilterFeatures([]);
                setFilterFreqRanges([]);
                setPage(1);
              }}
              labelRender={() => 'Xem theo'}
              style={{ minWidth: 150, maxWidth: '100%' }}
              className="xem-theo-select"
              suffixIcon={<DownOutlined style={{ color: '#fff' }} />}
              options={VIEW_BY_OPTIONS}
              dropdownMatchSelectWidth={false}
              placeholder="Xem theo"
            />
          </div>

          {/* 2. Chart Section */}
          <div css={headerStyle}>
            {/* Title */}
            <Title level={4} style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#000000' }}>
              {chartTitle}
            </Title>

            {/* Filter Row: Left = Chức năng hệ thống (and Tần suất truy cập lại), Right = Time buttons + RangePicker */}
            <div css={filterRowStyle}>
              {/* Left Sub-Filter(s) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {(viewBy === 'frequency_system' || viewBy === 'frequency_feature') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: 14, color: '#000000', whiteSpace: 'nowrap' }}>
                      Chức năng hệ thống
                    </span>
                    <Select
                      mode="multiple"
                      placeholder="Tất cả chức năng"
                      value={filterFeatures}
                      onChange={setFilterFeatures}
                      options={FEATURE_OPTIONS}
                      style={{ minWidth: 260, maxWidth: 360 }}
                      maxTagCount="responsive"
                      allowClear
                      dropdownMatchSelectWidth={false}
                    />
                  </div>
                )}
                {viewBy === 'frequency_feature' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: 14, color: '#000000', whiteSpace: 'nowrap' }}>
                      Tần suất truy cập lại
                    </span>
                    <Select
                      mode="multiple"
                      placeholder="Tất cả tần suất truy cập lại"
                      value={filterFreqRanges}
                      onChange={setFilterFreqRanges}
                      options={FREQUENCY_RANGE_OPTIONS}
                      style={{ minWidth: 220, maxWidth: 320 }}
                      maxTagCount="responsive"
                      allowClear
                      dropdownMatchSelectWidth={false}
                    />
                  </div>
                )}
              </div>

              {/* Right: Time Filters & RangePicker (căn phải) */}
              <div css={filterSectionStyle}>
                <Space size="small" wrap>
                  <Button
                    type={period === 'today' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('today')}
                  >
                    Hôm nay
                  </Button>
                  <Button
                    type={period === 'this_month' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('this_month')}
                  >
                    Tháng này
                  </Button>
                  <Button
                    type={period === 'this_year' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('this_year')}
                  >
                    Năm này
                  </Button>
                  <Button
                    type={period === 'all' ? 'primary' : 'text'}
                    onClick={() => handlePeriodChange('all')}
                  >
                    Tất cả
                  </Button>
                  <StatisticsDateRangePicker value={dateRange} onChange={applyDateRange}
                  showApply />
                </Space>
              </div>
            </div>

            {/* Legend (Figma Image 2) */}
            {viewBy === 'frequency_system' && chartData && (
              <div css={legendStyle}>
                <span css={legendBoxStyle} />
                <span style={{ fontSize: 13, color: '#595959' }}>
                  Số người dùng truy cập hệ thống
                </span>
              </div>
            )}
          </div>

          <Spin spinning={chartLoading}>
            {chartData ? (
              <ReactECharts
                ref={chartRef}
                option={chartOption}
                style={{ height: '500px', width: '100%' }}
                notMerge={true}
                lazyUpdate={false}
              />
            ) : (
              <div css={emptyStyle}>Không có dữ liệu</div>
            )}
          </Spin>
        </Card>

        {/* 3. Session Table */}
        <AccessSessionTable
          loading={listLoading}
          data={listData}
          page={page}
          pageSize={pageSize}
          onPageChange={(p, ps) => { setPage(p); setPageSize(ps); }}
        />
      </div>
    );
  }
);

const mainCardStyle = css`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .ant-card-body {
    padding: 16px 20px 24px 20px;
  }
`;

const viewByRowStyle = css`
  margin: 0 -20px 16px;
  padding: 0 20px 16px;
  border-bottom: 1px solid #f0f0f0;

  .xem-theo-select {
    display: inline-block;
  }

  .xem-theo-select .ant-select-selector {
    background-color: #1890ff !important;
    color: #fff !important;
    border-color: #1890ff !important;
    border-radius: 4px;
    font-weight: 600;
  }

  .xem-theo-select .ant-select-selection-item {
    color: #fff !important;
    font-weight: 600;
  }

  .xem-theo-select .ant-select-arrow {
    color: #fff !important;
  }
`;

const headerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 12px;
`;

const filterRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
`;

const filterSectionStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
`;

const legendStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 6px 0 2px 0;
`;

const legendBoxStyle = css`
  width: 10px;
  height: 10px;
  background-color: ${PRIMARY_BAR_COLOR};
  border-radius: 2px;
  display: inline-block;
`;

const emptyStyle = css`
  text-align: center;
  color: #bfbfbf;
  padding: 40px 0;
`;
